export type BlogPost = {
  id: string;
  title: string;
  date: string;
  body: BlogBlock[];
};

export type Span =
  | { kind: "text"; text: string }
  | { kind: "a"; href: string; label: string }
  | { kind: "code"; text: string };

export type Cell =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "num"; text: string };

export type BlogBlock =
  | { kind: "p"; text: string }
  | { kind: "p-spans"; spans: Span[] }
  | { kind: "h2"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ul-spans"; items: Span[][] }
  | {
      kind: "table";
      head: string[];
      rows: Cell[][];
    };

export const posts: BlogPost[] = [
  {
    id: "the-docs-diff-is-the-plan",
    title: "the docs diff is the plan",
    date: "2026-05-01",
    body: [
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "In the past month I tried out " },
          { kind: "a", href: "https://github.com/obra/superpowers", label: "superpowers" },
          { kind: "text", text: ", and I have been watching my coding agent working under this pattern: read a repo, write a spec, then a plan, and implement against the plan. This is a perfectly fine workflow for a software engineer, but it started to feel off to me for an agent. Does the agent really need to read the files so many times?" },
        ],
      },
      {
        kind: "p",
        text:
          "The spec and the plan are usually bespoke to one user request. They describe the system in the framing of that request, and then become disposable. Agent memory harness can usually at best remember what we have done, but not the system design concepts that we have discovered. When we start the next task, it has to rediscover those concepts from scratch, and that has become the bottleneck on every task.",
      },
      { kind: "h2", text: "dog" },
      {
        kind: "p",
        text:
          "Documentation Oriented Grammar, or DOG, is my attempt at fixing that. I want to see if we can make the concept layer the persistent artifact instead.",
      },
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "It is a small typed grammar over Markdown. Each " },
          { kind: "code", text: ".dog.md" },
          {
            kind: "text",
            text:
              " file defines one of four things — an Actor, a Behavior, a Component, or a Data shape — with a few required sections and typed cross-references. Sigils (",
          },
          { kind: "code", text: "@User" },
          { kind: "text", text: ", " },
          { kind: "code", text: "&Session" },
          { kind: "text", text: ", " },
          { kind: "code", text: "#AuthService" },
          { kind: "text", text: ", " },
          { kind: "code", text: "!Login" },
          { kind: "text", text: ") make the references machine-checkable, so the repo can be queried, linted, and diffed as a graph." },
        ],
      },
      {
        kind: "p-spans",
        spans: [
          {
            kind: "text",
            text:
              "The point is to keep it boring - so that the LLMs can use the little extra typed words, symbol links to and sections to make some structural sense out of it, while keeping it readable and reasonable as plain sentences. The CLI and skill live at ",
          },
          { kind: "a", href: "https://github.com/AirswitchAsa/dog", label: "AirswitchAsa/dog" },
          { kind: "text", text: "." },
        ],
      },

      { kind: "h2", text: "the workflow" },
      {
        kind: "p",
        text:
          "With DOG I started to do this: brainstorm a change → update the dog docs → review the diff → implement against that diff. The output of understanding is a durable map of the system rather than a plan that gets thrown away after one task.",
      },
      {
        kind: "p",
        text:
          "That sentence \"the diff is the plan\" is most of the point. If a docs diff already conveys what the change means, the agent has a much better starting point than it would from scratch.",
      },

      { kind: "h2", text: "the eval" },
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "There is not much point in claiming a methodology helps without at least a small controlled measurement. So I took the recently open-sourced " },
          { kind: "a", href: "https://github.com/warpdotdev/warp", label: "Warp" },
          { kind: "text", text: " repo and ran a small eval on the codebase, comparing a DOG-powered workflow against a source-only workflow." },
        ],
      },
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "The setup: same agent (" },
          { kind: "code", text: "claude-sonnet-4-6" },
          { kind: "text", text: "), five information-retrieval tasks against the " },
          { kind: "code", text: "warp_cli" },
          { kind: "text", text: " crate of " },
          { kind: "code", text: "warpdotdev/warp" },
          {
            kind: "text",
            text:
              ", three trials per condition. Condition A gets the warp source only. Condition B adds an authored dog docset and the dog skill.",
          },
        ],
      },
      { kind: "p", text: "Two design choices worth flagging:" },
      {
        kind: "ul",
        items: [
          "The dog docs and test cases were authored in a separate Claude Code session with no shared context with the eval runner, so the eval is not measuring whether the runner encoded the answers into the docs.",
          "Ground truth was extracted from the Rust source, not from the docs. B does not get credit for restating its own docs.",
        ],
      },
      { kind: "p", text: "The numbers, mean across the five tasks:" },
      {
        kind: "table",
        head: ["Metric", "Source only", "DOG docs + skill"],
        rows: [
          [
            { kind: "text", text: "Concept recall" },
            { kind: "num", text: "0.957" },
            { kind: "num", text: "0.991" },
          ],
          [
            { kind: "text", text: "File recall" },
            { kind: "num", text: "0.733" },
            { kind: "num", text: "0.967" },
          ],
          [
            { kind: "text", text: "Hallucinations" },
            { kind: "num", text: "0/15" },
            { kind: "num", text: "0/15" },
          ],
          [
            { kind: "text", text: "Tool calls / task" },
            { kind: "num", text: "19.7" },
            { kind: "num", text: "12.7" },
          ],
        ],
      },
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "The clearest delta is in file recall. Two of the five tasks had A missing the canonical file roughly two thirds of the time; " },
          { kind: "code", text: "rg" },
          { kind: "text", text: "-and-read foraging on a Rust crate does not always converge on the right spot. Under B, the path lives in the Notes block of the relevant primitive, and the agent stops guessing." },
        ],
      },
      {
        kind: "p",
        text:
          "Worth a small caveat: file paths are not the point on their own. They are a proxy for whether the agent reached the right conceptual neighborhood before reading code.",
      },
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "There is one anomaly worth recording. Task 03 spends more tool calls under B, not fewer. The question crosses the docset boundary — it asks about a workspace-" },
          { kind: "code", text: "Cargo.toml" },
          { kind: "text", text: " dependency that lives outside " },
          { kind: "code", text: "crates/warp_cli/" },
          { kind: "text", text: ". The agent looks in the docs first, does not find it, then falls back to grep. That's fair." },
        ],
      },

      { kind: "h2", text: "a tiny implementation task" },
      {
        kind: "p",
        text:
          "I also ran a small implementation task on the same crate — adding a flag to a subcommand, with a test. Both conditions completed it, and both passed the rubric on every trial (7/7). The interesting numbers are not the rubric:",
      },
      {
        kind: "table",
        head: ["Metric (mean, n=2)", "Source only", "DOG docs + skill"],
        rows: [
          [
            { kind: "text", text: "Tool calls / trial" },
            { kind: "num", text: "22.5" },
            { kind: "num", text: "12.0" },
          ],
          [
            { kind: "text", text: "Wall time / trial" },
            { kind: "num", text: "238 s" },
            { kind: "num", text: "148 s" },
          ],
          [
            { kind: "text", text: "Cost / trial" },
            { kind: "num", text: "$0.36" },
            { kind: "num", text: "$0.40" },
          ],
        ],
      },
      {
        kind: "p",
        text:
          "B finishes in roughly 62% of the wall time with about half the tool calls. Cost is roughly even, slightly higher under B; the dog skill loads around 30k tokens of context up front, and on a task this small the per-call savings do not quite cover that. In practice the savings show up as fewer tool calls.",
      },
      {
        kind: "p",
        text:
          "n = 2, and the rubric tests a narrow surface (one flag, one test), so these deltas are directional rather than significant. A more demanding implementation task — three or more files, less obvious touchpoints — would probably be a better test of the diff-is-the-plan workflow.",
      },

      { kind: "h2", text: "what this is not" },
      {
        kind: "p",
        text:
          "One codebase, one model, five IR tasks, and one underpowered implementation task. Nothing here proves that dog generalizes to bigger models or non-Rust repos, and I would rather not pretend otherwise.",
      },
      {
        kind: "p",
        text:
          "What I can say is that the lift in the measured tasks is real, the methodology is auditable, and the workflow has been pleasant enough on my own work that I am willing to put it in front of other people. Make of that what you like.",
      },
      {
        kind: "p-spans",
        spans: [
          { kind: "text", text: "Full methodology, raw transcripts, and limitations live at " },
          {
            kind: "a",
            href: "https://github.com/AirswitchAsa/dog-eval-warp",
            label: "AirswitchAsa/dog-eval-warp",
          },
        ],
      },
    ],
  },
];
